import { bindAll } from 'shared/decorators/bindAll';
import { nonenumerable } from 'shared/decorators/nonenumerable';
import { isJobInProgress } from 'lbs-pi-platform-access/es/data-server/image-analysis/jobs/util';
import { EventChannel } from 'shared/services/EventChannel';
import { injectable } from 'shared/stores/core/decorators';

const MONITOR_INTERVAL_MS = 1000;

@injectable('IA_JOB_MONITOR')
@bindAll
export class JobMonitor extends EventChannel {
    @nonenumerable timerId = null;
    @nonenumerable dispatcher = null;
    @nonenumerable jobProvider = null;
    @nonenumerable runningPromise = null;

    jobId = null;
    error = null;

    constructor ({dispatcher, resources}) {
      super();
      this.dispatcher = dispatcher;
      this.jobProvider = resources.get('IA_JOBS');
    }

    start () {
      if (this.jobId === null) {
        return Promise.reject(new Error('Cannot monitor a job without a job id.'));
      }

      if (this.isStarted) {
        return Promise.reject(new Error('Job monitor is already running.'));
      }

      return new Promise((resolve, reject) => {
        this.runningPromise = {resolve, reject};
        this.watch();
      });
    }

    get isStarted () {
      return !!this.runningPromise;
    }

    watch (timeout = 0) {
      // $10 says someone changes this to setInterval one day and then wonders why job statuses change
      // erratically on them...
      this.timerId = setTimeout(this.doWatch, timeout);
    }

    isWatching () {
      return this.timerId !== null;
    }

    @nonenumerable
    async doWatch () {
      try {
        const response = await this.jobProvider.getJobStatus(this.jobId);
        let jobState = response.data;

        if (!this.isWatching) {
          // If user stopped watching (paused or cancelled) after we got a response just exit.
          return;
        }

        if (!jobState) {
          // The "getJobStatus" API is... not ideal. It doesn't error when the requested job is not found, and really
          // a general purpose "not found" error would be a bit weird in this scenario since the only way it could
          // happen is if the image or associated IA layers were removed from under us, so we want a rather specific
          // error message anyway.
          return this.handleError(new Error('Analysis job was deleted from the server. Please refresh the viewer'));
        }

        this.trigger('update', jobState);

        if (isJobInProgress(jobState)) {
          this.watch(MONITOR_INTERVAL_MS);
        } else {
          this.timerId = null;
          this.runningPromise.resolve(jobState);
        }
      } catch (error) {
        this._handleError(error);
      }
    }

    handleError (error) {
      this.error = error;
      // TODO: Promote this to the coordinator
      this.dispatcher.dispatch('IA_JOB_ERROR', error);
      this.runningPromise.reject(error);
    }

    pause () {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    }

    resume () {
      if (!this.runningPromise) {
        return Promise.reject(new Error('Job monitor is not running.'));
      } else {
        this.watch();
        return this.runningPromise;
      }
    }
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/jobs/JobMonitor.js